import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Save, Trash2 } from "lucide-react";

// Horário global (fallback), retenção e status do agendador interno.
export function GlobalConfigCard({ savedHour, savedRetention, scheduler, onSaved, onReload }) {
  const [backupHour, setBackupHour] = useState(savedHour);
  const [retention, setRetention] = useState(savedRetention);
  const [saving, setSaving] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  useEffect(() => { setBackupHour(savedHour); setRetention(savedRetention); }, [savedHour, savedRetention]);
  const dirty = backupHour !== savedHour || retention !== savedRetention;

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const changes = {};
      if (backupHour !== savedHour) changes.backup_hour = backupHour;
      if (retention !== savedRetention) changes.retention_days = retention;
      await api.put("/backups/config", changes);
      onSaved(backupHour, retention);
      toast.success("Configuração salva — aplicada em até 1 minuto pelo scheduler");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const cleanupNow = async () => {
    if (!window.confirm(`Aplicar retenção de ${savedRetention} dias agora? Arquivos mais antigos serão APAGADOS (mantendo sempre o mais recente por empresa).`)) return;
    setCleaningUp(true);
    try {
      const r = await api.post("/backups/cleanup-now");
      const del = r.data.deleted?.length || 0;
      toast.success(del ? `${del} arquivo(s) apagado(s)` : "Nada pra apagar — todos dentro da janela de retenção");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no cleanup");
    } finally { setCleaningUp(false); }
  };

  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-600" />Agendamento global e retenção</CardTitle></CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Horário diário (BRT · UTC-3)</label>
          <Select value={String(backupHour)} onValueChange={(v) => setBackupHour(Number(v))}>
            <SelectTrigger data-testid="select-backup-hour"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 24 }, (_, i) => (
                <SelectItem key={`hour-${i}`} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1">Fallback para empresas sem horário próprio configurado abaixo. Dispara no minuto exato (HH:00).</p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Retenção (dias)</label>
          <Select value={String(retention)} onValueChange={(v) => setRetention(Number(v))}>
            <SelectTrigger data-testid="select-retention"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[7, 14, 30, 60, 90, 180, 365].map((d) => <SelectItem key={d} value={String(d)}>{d} dias</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground mt-1">Arquivos mais antigos são apagados automaticamente após o backup diário. O mais recente de cada empresa é sempre preservado.</p>
        </div>
        <div className="flex flex-col justify-end gap-2">
          <Button onClick={save} disabled={saving || !dirty} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-backup-hour">
            <Save className={`w-4 h-4 mr-2 ${saving ? "animate-pulse" : ""}`} />
            {saving ? "Salvando…" : "Salvar config"}
          </Button>
          <Button onClick={cleanupNow} disabled={cleaningUp} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="btn-cleanup-now">
            <Trash2 className={`w-4 h-4 mr-2 ${cleaningUp ? "animate-pulse" : ""}`} />
            {cleaningUp ? "Aplicando…" : "Aplicar retenção agora"}
          </Button>
        </div>
      </CardContent>
      {scheduler && <SchedulerStatus scheduler={scheduler} />}
    </Card>
  );
}

function SchedulerStatus({ scheduler }) {
  return (
    <div className="px-6 pb-4 -mt-1 text-[11px] text-muted-foreground flex items-center gap-2" data-testid="backup-scheduler-status" data-enabled={scheduler.enabled ? "true" : "false"}>
      <span className={`inline-block w-2 h-2 rounded-full ${scheduler.enabled && !scheduler.last_error ? "bg-emerald-500" : "bg-rose-500"}`} />
      Agendador interno {scheduler.enabled ? "ativo" : "desligado"}
      {scheduler.last_tick_at && <> · último ciclo {new Date(scheduler.last_tick_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</>}
      {scheduler.last_error && <span className="text-rose-600">· erro: {scheduler.last_error}</span>}
      <span>· cron externo <code>check-per-empresa-schedules</code> (a cada 1 min) + recuperação automática de horários perdidos após redeploy</span>
    </div>
  );
}
